import { useEffect, useState } from "react";
import { Link, useParams, useSearchParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { motion } from "framer-motion";
import { Loader2, MessageCircle } from "lucide-react";
import { EASE, Eyebrow, MaskLines } from "../components/site/Motion";
import { PhotoCard } from "../components/gallery/PhotoCard";
import { Lightbox } from "../components/gallery/Lightbox";
import { Pager } from "../components/gallery/Pager";
import { RecentlyViewed } from "../components/gallery/RecentlyViewed";
import { CategoryReviews } from "../components/gallery/CategoryReviews";
import { findCategory, waLink } from "../lib/catalog";
import { api, imgUrl, scrollToId } from "../lib/api";

const Intro = ({ cat, data }) => {
  const cover = cat.cover || (Array.isArray(data?.items) && data.items[0] ? imgUrl(data.items[0].url) : null);
  return (
  <section className="grain relative overflow-hidden bg-[#240620] pb-8 pt-[110px] md:pt-[160px]">
    <div className="pointer-events-none absolute -left-32 top-10 h-[420px] w-[420px] rounded-full bg-[#E91E63]/15 blur-[120px]" />
    <div className="relative mx-auto grid max-w-[1440px] items-end gap-10 px-5 sm:px-8 lg:grid-cols-12">
      <div className="lg:col-span-7">
        <Eyebrow>Collection</Eyebrow>
        <h1 key={cat.slug} className="mt-4 font-display text-6xl font-medium leading-[0.9] text-[#FFF8EF] sm:text-7xl lg:text-8xl" data-testid="gallery-title">
          <MaskLines onLoad lines={[cat.name]} />
        </h1>
        <p className="mt-5 max-w-xl text-base text-[#E8D8C8] sm:text-lg">{cat.desc}</p>
        {cat.note && <p className="mt-4 max-w-xl rounded-xl border border-[#D4AF37]/40 bg-[#D4AF37]/10 px-4 py-3 text-sm text-[#F3E5AB]" data-testid="gallery-category-note">{cat.note}</p>}
        <p className="mt-4 font-cinzel text-[11px] tracking-[0.3em] text-[#D4AF37]" data-testid="gallery-count">
          {data ? `${data.total} designs · page ${data.page} of ${data.pages}` : "Loading designs"}
        </p>
      </div>
      {cover && (
      <motion.div key={cover} initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 1, ease: EASE, delay: 0.2 }} className="lg:col-span-5" data-testid="gallery-flyer">
        <img src={cover} alt={`${cat.name} flyer`} className="mx-auto h-auto max-h-[75vh] w-auto max-w-full rounded-[24px] shadow-[0_40px_80px_-40px_rgba(0,0,0,0.8)] ring-1 ring-[#D4AF37]/50 lg:ml-auto lg:mr-0" />
      </motion.div>
      )}
    </div>
  </section>
  );
};

const Empty = ({ cat }) => (
  <div className="flex min-h-[420px] flex-col items-center justify-center gap-5 rounded-[28px] border border-dashed border-[#D4AF37]/25 px-6 text-center" data-testid="gallery-empty">
    <p className="font-display text-4xl italic text-[#F3E5AB]">Photos coming soon</p>
    <p className="max-w-md text-[#E8D8C8]">We're adding our {cat.name} work here. Message us to see recent designs.</p>
    <a href={waLink(`Hi SkandaSri Eventz, please share your ${cat.name} designs.`)} target="_blank" rel="noreferrer" className="inline-flex items-center gap-2 rounded-full bg-[#25D366] px-5 py-3 text-sm font-semibold text-[#06260f]" data-testid="gallery-empty-whatsapp">
      <MessageCircle size={16} /> Ask on WhatsApp
    </a>
  </div>
);

export default function Gallery() {
  const { slug } = useParams();
  const [sp, setSp] = useSearchParams();
  const page = Math.max(1, Number(sp.get("page")) || 1);
  const [open, setOpen] = useState(-1);
  const cat = findCategory(slug);

  const { data, isLoading, isError } = useQuery({
    queryKey: ["photos", slug, page],
    queryFn: () => api.get(`/categories/${slug}/photos`, { params: { page } }).then((r) => r.data),
    enabled: !!cat,
    placeholderData: (prev) => (prev?.slug === slug ? prev : undefined),
  });
  const items = Array.isArray(data?.items) ? data.items : [];
  const photoParam = sp.get("photo");

  useEffect(() => {
    if (!photoParam || !Array.isArray(data?.items)) return;
    const i = data.items.findIndex((p) => p.id === photoParam);
    if (i >= 0) setOpen(i);
  }, [photoParam, data]);

  const go = (p) => {
    setSp({ page: String(p) });
    scrollToId("gallery-grid", -150);
  };

  if (!cat) {
    return (
      <main className="flex min-h-[70vh] flex-col items-center justify-center gap-4 bg-[#240620] pt-32 text-center" data-testid="gallery-not-found">
        <h1 className="font-display text-5xl text-[#FFF8EF]">Collection not found</h1>
        <Link to="/" className="text-[#F3E5AB] underline">Back to home</Link>
      </main>
    );
  }

  return (
    <main data-testid="gallery-page">
      <Intro cat={cat} data={data} />
      <section id="gallery-grid" className="bg-[#190317] py-8 sm:py-12">
        <div className="mx-auto max-w-[1440px] px-4 sm:px-8">
          {isLoading && <div className="flex justify-center py-24 text-[#F3E5AB]"><Loader2 className="animate-spin" /></div>}
          {isError && <p className="py-24 text-center text-[#E8D8C8]" data-testid="gallery-error">We couldn't load the photos right now. Please try again shortly.</p>}
          {data && !items.length && <Empty cat={cat} />}
          <div className="grid grid-cols-2 gap-3 sm:gap-5 md:grid-cols-3 lg:grid-cols-4" data-testid="gallery-grid">
            {items.map((p, i) => <PhotoCard key={`${p.id}-${page}`} photo={p} index={i} catName={p.catName || cat.name} slug={p.slug || slug} onOpen={() => setOpen(i)} />)}
          </div>
          <Pager page={data?.page ?? page} pages={data?.pages ?? 1} onChange={go} />
          <p className="mt-8 text-center text-sm text-[#E8D8C8]" data-testid="gallery-disclaimer"><span className="font-semibold text-[#F3E5AB]">Please Note:</span> Images are for reference only. Design and flower combination remain the same, but slight variations may occur due to seasonal conditions.</p>
        </div>
      </section>
      {!cat.festive && <CategoryReviews cat={cat} />}
      <RecentlyViewed />
      <Lightbox items={items} index={open} setIndex={setOpen} catName={cat.name} slug={slug} page={data?.page ?? page} />
    </main>
  );
}
